/**
 * mountLogo — animated WebGL logo (port of legacy/js/logo.js).
 *
 * Draws a text texture to an offscreen canvas, then runs a layered sine-wave
 * displacement shader plus chromatic aberration. This is intentionally
 * untouched conceptually — only the export shape changes (was attached to
 * window.mountLogo, now an ES module export).
 */

export interface MountLogoOptions {
  lines?: string[];
  fontSize?: number;
  weight?: number;
  fontFamily?: string;
  color?: string;
  /** Extra space around text for displacement headroom. */
  padding?: number;
}

/** Mounts the animated logo into `container`. Returns a stop function. */
export async function mountLogo(
  container: HTMLElement,
  opts: MountLogoOptions = {},
): Promise<() => void> {
  const {
    lines = ["Big Wind"],
    fontSize = 80,
    weight = 400,
    fontFamily = '"BigSexySans", "Inter Display", sans-serif',
    color = "rgb(226, 229, 255)",
    padding = 48,
  } = opts;

  await document.fonts.ready;

  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  const FONT = `${weight} ${fontSize}px ${fontFamily}`;

  // Measure text on an offscreen ruler.
  const rulerCanvas = document.createElement("canvas");
  const ruler = rulerCanvas.getContext("2d");
  if (!ruler) return () => {};
  ruler.font = FONT;
  const lineH = Math.ceil(fontSize * 1.1);
  const textW = Math.ceil(Math.max(...lines.map((l) => ruler.measureText(l).width)));
  const textH = lineH * lines.length;

  const canvasW = textW + padding * 2;
  const canvasH = textH + padding * 2;

  // Draw text to the texture-source canvas.
  const off = document.createElement("canvas");
  off.width = canvasW * DPR;
  off.height = canvasH * DPR;
  const ctx = off.getContext("2d");
  if (!ctx) return () => {};
  ctx.scale(DPR, DPR);
  ctx.font = FONT;
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  lines.forEach((line, i) => {
    ctx.fillText(line, padding, padding + i * lineH);
  });

  // Display canvas mounted into container.
  const canvas = document.createElement("canvas");
  canvas.width = canvasW * DPR;
  canvas.height = canvasH * DPR;
  canvas.style.width = `${canvasW}px`;
  canvas.style.height = `${canvasH}px`;
  canvas.style.display = "block";
  container.appendChild(canvas);

  const gl = canvas.getContext("webgl", { alpha: true, premultipliedAlpha: false });
  if (!gl) {
    // Fallback: plain canvas text if WebGL unavailable.
    canvas.getContext("2d")?.drawImage(off, 0, 0);
    return () => {
      canvas.remove();
    };
  }

  const vs = `
    attribute vec2 aPos;
    varying vec2 vUV;
    void main() {
      vUV = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);
      gl_Position = vec4(aPos, 0.0, 1.0);
    }
  `;

  const fs = `
    precision mediump float;
    uniform sampler2D uTex;
    uniform float     uTime;
    varying vec2      vUV;

    vec2 warp(vec2 uv, float t) {
      float dx = sin(uv.x * 2.6 + t * 0.42) * 0.010
               + sin(uv.y * 2.1 + t * 0.31) * 0.006;
      float dy = sin(uv.y * 2.0 + t * 0.35) * 0.007
               + sin(uv.x * 2.8 + t * 0.24) * 0.005;

      dx += sin(uv.x * 6.5 + t * 0.47) * 0.010
          + sin(uv.y * 5.8 + t * 0.33) * 0.007
          + sin(uv.x * 5.0 - uv.y * 4.0 + t * 0.38) * 0.006;
      dy += sin(uv.y * 6.0 + t * 0.41) * 0.009
          + sin(uv.x * 7.0 + t * 0.29) * 0.006
          + sin(uv.x * 4.5 + uv.y * 5.5 + t * 0.44) * 0.005;

      dx += sin(uv.x * 14.0 + sin(uv.y * 9.0 + t * 0.5) + t * 0.52) * 0.007
          + sin(uv.y * 12.0 + sin(uv.x * 8.0 + t * 0.4) + t * 0.37) * 0.005;
      dy += sin(uv.y * 13.0 + sin(uv.x * 10.0 + t * 0.45) + t * 0.48) * 0.007
          + sin(uv.x * 11.0 + sin(uv.y * 7.0  + t * 0.35) + t * 0.41) * 0.005;

      return vec2(dx, dy);
    }

    void main() {
      vec2  d  = warp(vUV, uTime);
      float ca = 0.003;
      float r  = texture2D(uTex, vUV + d + vec2( ca, 0.0)).r;
      float g  = texture2D(uTex, vUV + d              ).g;
      float b  = texture2D(uTex, vUV + d - vec2( ca, 0.0)).b;
      float a  = texture2D(uTex, vUV + d              ).a;
      gl_FragColor = vec4(r, g, b, a);
    }
  `;

  function compileShader(type: number, src: string): WebGLShader {
    const sh = gl!.createShader(type);
    if (!sh) throw new Error("createShader failed");
    gl!.shaderSource(sh, src);
    gl!.compileShader(sh);
    if (!gl!.getShaderParameter(sh, gl!.COMPILE_STATUS)) {
      throw new Error(`Shader error: ${gl!.getShaderInfoLog(sh)}`);
    }
    return sh;
  }

  const prog = gl.createProgram();
  if (!prog) {
    canvas.remove();
    return () => {};
  }
  gl.attachShader(prog, compileShader(gl.VERTEX_SHADER, vs));
  gl.attachShader(prog, compileShader(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(`Program link error: ${gl.getProgramInfoLog(prog)}`);
  }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW,
  );
  const aPos = gl.getAttribLocation(prog, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, off);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.uniform1i(gl.getUniformLocation(prog, "uTex"), 0);

  const uTime = gl.getUniformLocation(prog, "uTime");

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.viewport(0, 0, canvas.width, canvas.height);

  const start = performance.now();
  let raf: number | null = null;
  const frame = () => {
    gl.uniform1f(uTime, (performance.now() - start) / 1000);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    raf = requestAnimationFrame(frame);
  };
  frame();

  return () => {
    if (raf !== null) cancelAnimationFrame(raf);
    canvas.remove();
  };
}
